/**
 * Problem Report Detail Page - Admin view with status management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Loader2, ArrowLeft, Clock, User as UserIcon,
  Building2, Globe, Monitor, Image, CheckCircle, X, Save, UserPlus
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { API_ENDPOINTS } from '../../config/api';
import { getToken } from '../../utils/tokenManager';
import {
  ProblemReportStatus,
  SeverityLabels,
  CategoryLabels,
  StatusLabels,
  SeverityColors,
  StatusColors,
  AdminProblemReportResponse
} from '../../types/problemReport';
import { getErrorMessage } from '../../services/apiService';

const ProblemReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isClientAdmin = user?.role === 'ClientAdmin';
  const isSystemAdmin = user?.isSystemAdmin === true;

  // Redirect if not authorized
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
    } else if (!isClientAdmin && !isSystemAdmin) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, isClientAdmin, isSystemAdmin]);

  const [report, setReport] = useState<AdminProblemReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Edit state
  const [editedStatus, setEditedStatus] = useState<ProblemReportStatus | null>(null);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showScreenshot, setShowScreenshot] = useState(false);

  const loadReport = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_GET(id), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Problem report not found');
        }
        throw new Error('Failed to load problem report');
      }

      const data: AdminProblemReportResponse = await response.json();
      setReport(data);
      setEditedStatus(data.status);
      setEditedNotes(data.adminNotes || '');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (user && (isClientAdmin || isSystemAdmin)) {
      loadReport();
    }
  }, [user, isClientAdmin, isSystemAdmin, loadReport]);

  const handleStatusChange = async () => {
    if (!id || editedStatus === null || editedStatus === report?.status) return;

    try {
      setIsSaving(true);
      setError('');

      const token = getToken();
      if (!token) return;

      const response = await fetch(API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_UPDATE_STATUS(id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: editedStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      setSuccess('Status updated successfully');
      loadReport();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotesChange = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      setError('');

      const token = getToken();
      if (!token) return;

      const response = await fetch(API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_UPDATE_NOTES(id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminNotes: editedNotes })
      });

      if (!response.ok) {
        throw new Error('Failed to update notes');
      }

      setSuccess('Notes saved successfully');
      loadReport();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!id || !user?.id) return;

    try {
      setIsSaving(true);
      setError('');

      const token = getToken();
      if (!token) return;

      const response = await fetch(API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_ASSIGN(id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignedToUserId: user.id })
      });

      if (!response.ok) {
        throw new Error('Failed to assign report');
      }

      setSuccess('Report assigned to you');
      loadReport();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f0]">
        <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-xl p-8 text-center border border-black/[0.08]">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500 opacity-50" />
              <h2 className="text-xl font-bold text-[#0f172a] mb-2">Report Not Found</h2>
              <p className="text-[#64748b] mb-4">{error || 'The requested problem report could not be found.'}</p>
              <button
                onClick={() => navigate('/admin/problem-reports')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#c41e3a] text-white rounded-lg hover:bg-[#a31830] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Reports
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f0]">
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/admin/problem-reports')}
            className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Reports
          </button>

          {/* Alerts */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-green-700">{success}</span>
              <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header Card */}
          <div className="glass-card rounded-xl p-6 mb-6 border border-black/[0.08]">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#c41e3a] to-red-700 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#0f172a]">Problem Report</h1>
                  <p className="text-sm text-[#64748b]">ID: {report.id.substring(0, 8)}...</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full border ${StatusColors[report.status]}`}>
                  {StatusLabels[report.status]}
                </span>
                <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full border ${SeverityColors[report.severity]}`}>
                  {SeverityLabels[report.severity]}
                </span>
              </div>
            </div>

            {/* Meta Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-[#64748b]">
                <Clock className="w-4 h-4" />
                <span>{formatDate(report.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-[#64748b]">
                <UserIcon className="w-4 h-4" />
                <span>{report.userName}</span>
              </div>
              {report.clientName && (
                <div className="flex items-center gap-2 text-[#64748b]">
                  <Building2 className="w-4 h-4" />
                  <span>{report.clientName}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-[#64748b]">
                <span className="font-medium">Category:</span>
                <span>{CategoryLabels[report.category]}</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-6">
              {/* Description */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Description</h2>
                <p className="text-[#475569] whitespace-pre-wrap">{report.description}</p>
              </div>

              {/* Technical Details */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Technical Details</h2>
                <div className="space-y-3">
                  {report.pageUrl && (
                    <div>
                      <div className="flex items-center gap-2 text-xs font-medium text-[#64748b] mb-1">
                        <Globe className="w-3 h-3" />
                        Page URL
                      </div>
                      <p className="text-sm text-[#475569] bg-black/[0.02] px-3 py-2 rounded-lg break-all">
                        {report.pageUrl}
                      </p>
                    </div>
                  )}
                  {report.userAgent && (
                    <div>
                      <div className="flex items-center gap-2 text-xs font-medium text-[#64748b] mb-1">
                        <Monitor className="w-3 h-3" />
                        Browser/Device
                      </div>
                      <p className="text-sm text-[#475569] bg-black/[0.02] px-3 py-2 rounded-lg break-all">
                        {report.userAgent}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Screenshot */}
              {report.hasScreenshot && report.screenshotData && (
                <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-[#0f172a] flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Screenshot
                    </h2>
                    <button
                      onClick={() => setShowScreenshot(!showScreenshot)}
                      className="text-sm text-[#c41e3a] hover:underline"
                    >
                      {showScreenshot ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showScreenshot && (
                    <div className="rounded-lg overflow-hidden border border-black/[0.08]">
                      <img
                        src={`data:${report.screenshotContentType || 'image/png'};base64,${report.screenshotData}`}
                        alt="Screenshot"
                        className="w-full h-auto"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Status Management */}
            <div className="space-y-6">
              {/* Status Update */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Update Status</h2>
                <div className="space-y-4">
                  <select
                    value={editedStatus ?? report.status}
                    onChange={(e) => setEditedStatus(Number(e.target.value) as ProblemReportStatus)}
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all"
                  >
                    {Object.entries(StatusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleStatusChange}
                    disabled={isSaving || editedStatus === report.status}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#c41e3a] text-white rounded-lg hover:bg-[#a31830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Update Status
                  </button>
                </div>
              </div>

              {/* Assignment */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Assignment</h2>
                {report.assignedToName ? (
                  <div className="flex items-center gap-2 text-[#475569] bg-black/[0.02] px-3 py-2 rounded-lg">
                    <UserIcon className="w-4 h-4" />
                    <span>{report.assignedToName}</span>
                  </div>
                ) : (
                  <button
                    onClick={handleAssignToMe}
                    disabled={isSaving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#c41e3a] text-[#c41e3a] rounded-lg hover:bg-[#c41e3a]/10 transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" />
                    Assign to Me
                  </button>
                )}
              </div>

              {/* Admin Notes */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Admin Notes</h2>
                <div className="space-y-4">
                  <textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    rows={4}
                    placeholder="Add internal notes about this report..."
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a] transition-all resize-none"
                    maxLength={4000}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#94a3b8]">{editedNotes.length}/4000</span>
                    <button
                      onClick={handleNotesChange}
                      disabled={isSaving || editedNotes === (report.adminNotes || '')}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0f172a] text-white rounded-lg hover:bg-[#1e293b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      Save Notes
                    </button>
                  </div>
                </div>
              </div>

              {/* User Contact */}
              <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
                <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Reporter Info</h2>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-[#64748b]">Name:</span>
                    <span className="ml-2 text-[#0f172a]">{report.userName}</span>
                  </div>
                  <div>
                    <span className="text-[#64748b]">Email:</span>
                    <a href={`mailto:${report.userEmail}`} className="ml-2 text-[#c41e3a] hover:underline">
                      {report.userEmail}
                    </a>
                  </div>
                  {report.clientName && (
                    <div>
                      <span className="text-[#64748b]">Organization:</span>
                      <span className="ml-2 text-[#0f172a]">{report.clientName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProblemReportDetail;
